import {createSelector, createEntityAdapter, EntityState, Draft} from '@reduxjs/toolkit'
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react'
import {RootState} from '../../../data/redux/Store'
import {AUTH_TOKEN_KEY, env} from '../../../data/constants'
import {IPaginationMetadata, ISearchEntities} from '../shared/interfaces'
import {IGoal, IGrantProviderDetails} from './interfaces/IGrantProviderDetails'
import {IGrantOpportunity} from './interfaces/IGrantOpportunity'

const GrantProvidersAdapter = createEntityAdapter<IGrantProviderDetails>({
  selectId: (GrantProvider) => GrantProvider.id,
})
// State getters

const GrantProvidersInitialState = GrantProvidersAdapter.getInitialState()

const baseQuery = fetchBaseQuery({
  baseUrl: env.GrantProviders,
  validateStatus: (response, result) => {
    if (result && result.isError) return false
    if (response.status >= 200 && response.status <= 300) return true
    if (response.status >= 400 && response.status <= 500) return false
    return false
  },
  prepareHeaders: (headers, {getState}) => {
    const accessToken = localStorage.getItem(AUTH_TOKEN_KEY)
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`)
    }
    return headers
  },
})

const customBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await baseQuery(args, api, extraOptions)
  if (result.error) {
    console.error('Fetch error:', result.error)
  }
  return result
}

export const GrantProvidersApiSlice = createApi({
  reducerPath: 'GrantProvidersApi',
  baseQuery: customBaseQuery,

  tagTypes: ['Financiers', 'GrantProvider', 'GrantProviderDetails', 'Goal', 'GrantOpportunity'],
  endpoints: (builder) => ({
    getGrantProviders: builder.query<
      {entities: EntityState<IGrantProviderDetails>; paginationMetadata: IPaginationMetadata},
      ISearchEntities
    >({
      query: (searchParams) => {
        const params = new URLSearchParams()
        if (searchParams.page) params.append('page', searchParams.page.toString())
        if (searchParams.pageSize) params.append('pageSize', searchParams.pageSize.toString())
        if (searchParams.searchTerm) params.append('searchTerm', searchParams.searchTerm)
        if (searchParams.countries) {
          searchParams.countries.forEach((country) => params.append('countries', country))
        }
        if (searchParams.totalBudget) {
          params.append('totalBudget', searchParams.totalBudget.toString())
        }
        if (searchParams.areaOfSupport) {
          params.append('areaOfSupport', searchParams.areaOfSupport)
        }
        if (searchParams.organisationType) {
          params.append('organizationType', searchParams.organisationType)
        }
        return {
          url: '/development/partners',
          params: params,
          method: 'GET',
        }
      },

      transformResponse: (response: any, meta: FetchBaseQueryMeta) => {
        const entities = GrantProvidersAdapter.setAll(GrantProvidersInitialState, response.data)
        const paginationMetadata = response.paginationMetadata
        const pagination: IPaginationMetadata = paginationMetadata
          ? {
              totalCount: paginationMetadata.totalItems || 0,
              page: paginationMetadata.currentPage || 1,
              pageSize: paginationMetadata.pageSize || 9,
              hasNextPage: paginationMetadata.hasNextPage || false,
              hasPreviousPage: paginationMetadata.hasPreviousPage || false,
            }
          : {
              totalCount: 0,
              page: 1,
              pageSize: 9,
              hasNextPage: false,
              hasPreviousPage: false,
            }
        return {
          entities: entities,
          paginationMetadata: pagination,
        }
      },
      providesTags: (result: {entities: EntityState<IGrantProviderDetails>} | undefined) => {
        if (result?.entities.ids) {
          return [
            {type: 'GrantProvider', id: 'LIST'},
            ...result.entities.ids.map((id) => ({type: 'GrantProvider' as const, id})),
          ]
        } else {
          return [{type: 'GrantProvider', id: 'LIST'}]
        }
      },
    }),
    getGrantProviderDetails: builder.query<EntityState<IGrantProviderDetails>, string>({
      query: (id) => `/development/partners/${id}`,
      // transformResponse: (res: any) => {
      //   return GrantProvidersAdapter.setOne(GrantProvidersInitialState, res)
      // },
      providesTags: (result: EntityState<IGrantProviderDetails> | undefined) => {
        if (result?.ids) {
          return [...result.ids.map((id) => ({type: 'Financiers' as const, id}))]
        } else {
          return [{type: 'Financiers', id: 'LIST'}]
        }
      },
    }),

    addNewGrantProvider: builder.mutation({
      query: (data: IGrantProviderDetails) => ({
        url: '/development/partners',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'GrantProvider', id: 'LIST'}],
    }),
    updateGrantProvider: builder.mutation({
      query: (updatedPartnerData) => ({
        url: `/development/partners`,
        method: 'PUT',
        body: updatedPartnerData,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'GrantProvider',
          id: arg.id,
        },
        {type: 'GrantProviderDetails', id: arg.id},
      ],
    }),
    deleteGrantProvider: builder.mutation({
      query: (id) => ({
        url: `/development/partners/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'GrantProvider', id: arg.id}],
    }),
    addGoal: builder.mutation({
      query: (data: IGoal) => ({
        url: '/development/goals',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'Goal', id: 'LIST'}],
    }),
    updateGoal: builder.mutation({
      query: (data: IGoal) => ({
        url: `/development/goals`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Goal', id: arg.id ?? 'LIST'},
        {type: 'GrantProviderDetails', id: arg.developmentPartnerId ?? 'LIST'},
      ],
    }),
    // Grant Opportunity Endpoints
    getGrantOpportunities: builder.query<IGrantOpportunity[], void>({
      query: () => {
        const params = new URLSearchParams()
        params.append('page', '1')
        params.append('pageSize', '1000')
        return {
          url: '/grant-opportunities/search',
          params: params,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        return response.data || []
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({id}) => ({type: 'GrantOpportunity' as const, id})),
              {type: 'GrantOpportunity', id: 'LIST'},
            ]
          : [{type: 'GrantOpportunity', id: 'LIST'}],
    }),
    getGrantOpportunityById: builder.query<IGrantOpportunity, string>({
      query: (id) => ({
        url: `/grant-opportunities/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{type: 'GrantOpportunity', id}],
    }),
    createGrantOpportunity: builder.mutation<IGrantOpportunity, Partial<IGrantOpportunity>>({
      query: (data) => ({
        url: '/grant-opportunities',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{type: 'GrantOpportunity', id: 'LIST'}],
    }),
    updateGrantOpportunity: builder.mutation<IGrantOpportunity, IGrantOpportunity>({
      query: (data) => ({
        url: '/grant-opportunities',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'GrantOpportunity', id: arg.id},
        {type: 'GrantOpportunity', id: 'LIST'},
      ],
    }),
    deleteGrantOpportunity: builder.mutation<{message: string}, string>({
      query: (id) => ({
        url: `/grant-opportunities/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        {type: 'GrantOpportunity', id},
        {type: 'GrantOpportunity', id: 'LIST'},
      ],
    }),
    publishGrantOpportunity: builder.mutation<IGrantOpportunity, string>({
      query: (id) => ({
        url: `/grant-opportunities/${id}/publish`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, id) => [
        {type: 'GrantOpportunity', id},
        {type: 'GrantOpportunity', id: 'LIST'},
      ],
    }),
  }),
})

export const {
  useGetGrantProvidersQuery,
  useAddNewGrantProviderMutation,
  useUpdateGrantProviderMutation,
  useGetGrantProviderDetailsQuery,
  useDeleteGrantProviderMutation,
  useAddGoalMutation,
  useUpdateGoalMutation,
  useGetGrantOpportunitiesQuery,
  useGetGrantOpportunityByIdQuery,
  useCreateGrantOpportunityMutation,
  useUpdateGrantOpportunityMutation,
  useDeleteGrantOpportunityMutation,
  usePublishGrantOpportunityMutation,
} = GrantProvidersApiSlice

//Financiers selectors

export const selectGrantProvidersResult = (state: RootState, searchParams: ISearchEntities) =>
  GrantProvidersApiSlice.endpoints.getGrantProviders.select(searchParams)(state)

export const selectGrantProvidersData = (searchParams: ISearchEntities) =>
  createSelector(
    (state: RootState) => selectGrantProvidersResult(state, searchParams),
    (GrantProvidersResult) => GrantProvidersResult?.data?.entities ?? GrantProvidersInitialState
  )

export const createGrantProvidersSelectors = (searchParams: ISearchEntities) => {
  const selectData = selectGrantProvidersData(searchParams)
  return GrantProvidersAdapter.getSelectors((state: RootState) => selectData(state))
}
